const PUBLIC_FEATURES = [
  { key: "home", label: "Home", viewEvents: ["public_home_view"] },
  { key: "marketplace", label: "Marketplace", viewEvents: ["public_marketplace_view"] },
  { key: "farm_stands", label: "Farm Stands / Pickup Spots", viewEvents: ["public_farm_stands_view", "public_stand_open"] },
  { key: "live_shelf", label: "Live Shelf", viewEvents: ["public_live_shelf_view", "public_live_shelf_item_open"] },
  { key: "events", label: "Events", viewEvents: ["public_events_view", "public_event_open"] },
  { key: "jobs", label: "Jobs", viewEvents: ["public_jobs_view", "public_job_open"] },
  { key: "plants_garden", label: "Plants & Garden", viewEvents: ["public_plants_garden_view"] },
  { key: "land_share", label: "Land Share", viewEvents: ["public_land_share_view", "public_land_share_open"] },
  { key: "other_account", label: "Other / Account", viewEvents: [] },
];

export const REPORTING_TIME_ZONE = "America/Chicago";
export const FALL_CRAWL_EVENT_DAY_DATE = "2026-09-26";
export const FALL_CRAWL_EVENT_DAY_TIME_ZONE = "America/Chicago";
export const PUBLIC_VISITOR_RAW_RANGE_MAX_DAYS = 31;
export const FALL_CRAWL_ANALYTICS_FEATURE = "fall_crawl";

export const FALL_CRAWL_ANALYTICS_ACTIONS = {
  mapOpen: "map_open",
  stopView: "stop_view",
  directionsClick: "directions_click",
  backroadsBookOpen: "backroads_book_open",
  localLiveOpen: "local_live_open",
  routeInteraction: "route_interaction",
};

export const PUBLIC_VISITOR_RANGE_OPTIONS = [
  { key: "today", label: "Today" },
  { key: "specific-day", label: "Specific Day" },
  { key: "7-days", label: "7 Days" },
  { key: "30-days", label: "30 Days" },
  { key: "60-days", label: "60 Days" },
  { key: "1-year", label: "1 Year" },
  { key: "lifetime", label: "Lifetime" },
  { key: "custom", label: "Custom Range" },
  { key: "fall-crawl-event-day", label: "Fall Crawl Event Day" },
];

export const PUBLIC_VISITOR_EVENT_TYPES = new Set([
  "public_page_view",
  "public_home_view",
  "public_marketplace_view",
  "public_product_open",
  "public_farm_stands_view",
  "public_stand_open",
  "public_live_shelf_view",
  "public_live_shelf_item_open",
  "public_events_view",
  "public_event_open",
  "public_jobs_view",
  "public_job_open",
  "public_plants_garden_view",
  "public_land_share_view",
  "public_land_share_open",
]);

const ACTION_EVENTS = new Set([
  "auth_gate_open",
  "signup_click",
  "signin_click",
  "request_to_buy_intent",
  "message_seller_intent",
  "job_apply_intent",
  "job_bid_intent",
  "event_participation_intent",
  "land_share_contact_or_post_intent",
]);

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const getZonedDateParts = (value, timeZone = REPORTING_TIME_ZONE) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(value));

  return parts.reduce((result, part) => {
    if (part.type !== "literal") result[part.type] = Number(part.value);
    return result;
  }, {});
};

const getTimeZoneOffsetMs = (value, timeZone = REPORTING_TIME_ZONE) => {
  const parts = getZonedDateParts(value, timeZone);
  const displayedAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  return displayedAsUtc - new Date(value).getTime();
};

const getLocalMidnightMs = (dateParts, timeZone = REPORTING_TIME_ZONE) => {
  const utcGuess = Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day);
  return utcGuess - getTimeZoneOffsetMs(utcGuess, timeZone);
};

const shiftCalendarDate = (dateParts, days) => {
  const shifted = new Date(Date.UTC(
    dateParts.year,
    dateParts.month - 1,
    dateParts.day + days
  ));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
};

const padDatePart = (value) => String(value).padStart(2, "0");

const toDateId = ({ year, month, day } = {}) =>
  `${year}-${padDatePart(month)}-${padDatePart(day)}`;

const parseDateId = (value = "") => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const parts = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
  const roundTrip = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  if (
    roundTrip.getUTCFullYear() !== parts.year ||
    roundTrip.getUTCMonth() + 1 !== parts.month ||
    roundTrip.getUTCDate() !== parts.day
  ) {
    return null;
  }
  return parts;
};

export const getPublicVisitorDateId = (now = Date.now(), timeZone = REPORTING_TIME_ZONE) =>
  toDateId(getZonedDateParts(now, timeZone));

export const getPublicVisitorDayPeriod = (
  dateId,
  timeZone = REPORTING_TIME_ZONE,
  label = dateId
) => {
  const startParts = parseDateId(dateId);
  if (!startParts) {
    return {
      error: "Choose a valid reporting date.",
      label,
      queryMode: "raw",
      timeZone,
    };
  }
  const endParts = shiftCalendarDate(startParts, 1);
  const startMs = getLocalMidnightMs(startParts, timeZone);
  const endMs = getLocalMidnightMs(endParts, timeZone) - 1;
  return {
    days: 1,
    endDate: dateId,
    endMs,
    label,
    queryMode: "raw",
    rangeKey: "specific-day",
    startDate: dateId,
    startMs,
    timeZone,
  };
};

const getCalendarRangeDayCount = (startDate, endDate) => {
  const startParts = parseDateId(startDate);
  const endParts = parseDateId(endDate);
  if (!startParts || !endParts) return 0;
  const startUtc = Date.UTC(startParts.year, startParts.month - 1, startParts.day);
  const endUtc = Date.UTC(endParts.year, endParts.month - 1, endParts.day);
  return Math.floor((endUtc - startUtc) / 86400000) + 1;
};

const getCalendarRangePeriod = ({
  startDate,
  endDate,
  label,
  rangeKey = "custom",
  timeZone = REPORTING_TIME_ZONE,
  preferRaw = false,
} = {}) => {
  const startParts = parseDateId(startDate);
  const endParts = parseDateId(endDate);
  if (!startParts || !endParts) {
    return {
      error: "Choose a valid start and end date.",
      label,
      queryMode: preferRaw ? "raw" : "daily",
      rangeKey,
      timeZone,
    };
  }
  const rangeDays = getCalendarRangeDayCount(startDate, endDate);
  if (rangeDays <= 0) {
    return {
      error: "Choose an end date that is on or after the start date.",
      label,
      queryMode: preferRaw ? "raw" : "daily",
      rangeKey,
      timeZone,
    };
  }

  const nextEndParts = shiftCalendarDate(endParts, 1);
  const startMs = getLocalMidnightMs(startParts, timeZone);
  const endMs = getLocalMidnightMs(nextEndParts, timeZone) - 1;
  return {
    days: rangeDays,
    endDate,
    endMs,
    label,
    queryMode: preferRaw || rangeDays <= PUBLIC_VISITOR_RAW_RANGE_MAX_DAYS ? "raw" : "daily",
    rangeKey,
    startDate,
    startMs,
    timeZone,
  };
};

export const getPublicVisitorReportingPeriod = (days = 7, now = Date.now()) => {
  const normalizedDays = [1, 7, 30].includes(Number(days)) ? Number(days) : 7;
  const endMs = new Date(now).getTime();
  const todayParts = getZonedDateParts(endMs);
  const startParts = shiftCalendarDate(todayParts, -(normalizedDays - 1));

  return {
    days: normalizedDays,
    timeZone: REPORTING_TIME_ZONE,
    startMs: getLocalMidnightMs(startParts),
    endMs,
    label: normalizedDays === 1 ? "Today" : `${normalizedDays} days`,
  };
};

export const getPublicVisitorReportingRange = ({
  rangeKey = "7-days",
  specificDate = "",
  customStartDate = "",
  customEndDate = "",
} = {}, now = Date.now()) => {
  const endMs = new Date(now).getTime();
  const todayDate = getPublicVisitorDateId(endMs);

  if (rangeKey === "today") {
    return {
      ...getPublicVisitorDayPeriod(todayDate, REPORTING_TIME_ZONE, "Today"),
      rangeKey,
    };
  }

  if (rangeKey === "specific-day") {
    return {
      ...getPublicVisitorDayPeriod(specificDate || todayDate, REPORTING_TIME_ZONE, "Specific Day"),
      rangeKey,
    };
  }

  if (rangeKey === "fall-crawl-event-day") {
    return {
      ...getPublicVisitorDayPeriod(
        FALL_CRAWL_EVENT_DAY_DATE,
        FALL_CRAWL_EVENT_DAY_TIME_ZONE,
        "Fall Crawl Event Day"
      ),
      rangeKey,
    };
  }

  if (rangeKey === "custom") {
    return getCalendarRangePeriod({
      startDate: customStartDate,
      endDate: customEndDate,
      label: "Custom Range",
      rangeKey,
    });
  }

  if (rangeKey === "60-days" || rangeKey === "1-year") {
    const days = rangeKey === "1-year" ? 365 : 60;
    const todayParts = getZonedDateParts(endMs);
    const startParts = shiftCalendarDate(todayParts, -(days - 1));
    const startDate = toDateId(startParts);
    const endDate = toDateId(todayParts);
    return {
      days,
      endDate,
      endMs,
      label: rangeKey === "1-year" ? "1 Year" : "60 Days",
      queryMode: "daily",
      rangeKey,
      startDate,
      startMs: getLocalMidnightMs(startParts),
      timeZone: REPORTING_TIME_ZONE,
    };
  }

  if (rangeKey === "lifetime") {
    return {
      days: null,
      endDate: todayDate,
      endMs,
      label: "Lifetime",
      queryMode: "daily",
      rangeKey,
      startDate: "",
      startMs: null,
      timeZone: REPORTING_TIME_ZONE,
    };
  }

  const days = rangeKey === "30-days" ? 30 : 7;
  return {
    ...getPublicVisitorReportingPeriod(days, now),
    endDate: todayDate,
    queryMode: "raw",
    rangeKey: `${days}-days`,
    startDate: getPublicVisitorDateId(getPublicVisitorReportingPeriod(days, now).startMs),
  };
};

const normalizeSource = (event) => {
  const source = String(event.utm_source || "").trim().toLowerCase();
  if (source.includes("facebook") || source === "fb") return "Facebook";
  if (source.includes("tiktok")) return "TikTok";
  if (source) return "Other";

  const referrer = String(event.referrer || "").toLowerCase();
  if (!referrer) return "Direct";
  if (referrer.includes("facebook.com")) return "Facebook";
  if (referrer.includes("tiktok.com")) return "TikTok";
  return "Other";
};

const featureForEvent = (event) => {
  const feature = String(event.feature || "").toLowerCase();
  const route = String(event.path || "").toLowerCase();
  if (feature === "home" || feature.includes("public_home")) return "home";
  if (feature.includes("market") || route === "/marketplace") return "marketplace";
  if (feature.includes("shelf")) return "live_shelf";
  if (feature.includes("stand") || route.startsWith("/farm-stands") || route.startsWith("/stand/")) {
    return route.startsWith("/stand/") || feature.includes("shelf") ? "live_shelf" : "farm_stands";
  }
  if (feature.includes("event") || route.startsWith("/events")) return "events";
  if (feature.includes("job") || route.startsWith("/jobs")) return "jobs";
  if (feature.includes("plant") || route.startsWith("/plants-garden")) return "plants_garden";
  if (feature.includes("land") || route.startsWith("/land-share")) return "land_share";
  if (route === "/marketplace") return "marketplace";
  if (route.startsWith("/farm-stands") || route.startsWith("/stand/")) {
    return route.startsWith("/stand/") ? "live_shelf" : "farm_stands";
  }
  if (route.startsWith("/events")) return "events";
  if (route.startsWith("/jobs")) return "jobs";
  if (route.startsWith("/plants-garden")) return "plants_garden";
  if (route.startsWith("/land-share")) return "land_share";
  return "other_account";
};

const countUniqueSessions = (events) => new Set(
  events.map((event) => event.sessionId).filter(Boolean)
).size;

const isFallCrawlInteractionEvent = (event = {}) =>
  event.eventType === "event_participation_intent" &&
  String(event.feature || "").toLowerCase() === FALL_CRAWL_ANALYTICS_FEATURE;

const isPublicVisitorActionEvent = (event = {}) =>
  ACTION_EVENTS.has(event.eventType) && !isFallCrawlInteractionEvent(event);

const countFallCrawlAction = (events, action) =>
  events.filter((event) => event.action === action).length;

const getFallCrawlStopLabel = (event = {}) => {
  const routeName = String(event.routeName || "").trim();
  if (routeName && routeName !== "Fall Crawl 2026" && routeName !== "Local Live") return routeName;
  return String(event.relatedId || "Unknown stop").trim() || "Unknown stop";
};

const buildTopFallCrawlStops = (events = [], action) => {
  const countsByStop = events
    .filter((event) => event.action === action && event.relatedId)
    .reduce((counts, event) => {
      const stopId = event.relatedId;
      const current = counts.get(stopId) || { stopId, label: getFallCrawlStopLabel(event), count: 0 };
      counts.set(stopId, {
        ...current,
        label: current.label === stopId ? getFallCrawlStopLabel(event) : current.label,
        count: current.count + 1,
      });
      return counts;
    }, new Map());

  return [...countsByStop.values()]
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, 10);
};

const getStopIdFromRow = (row = {}) =>
  String(row.stopId || row.id || "").trim();

const buildFallCrawlFullStopRows = (events = [], authoritativeEventDayMetrics = null) => {
  const rowsByStop = new Map();
  const ensureRow = (stopId, label = "", metadata = {}) => {
    const normalizedStopId = String(stopId || "").trim();
    if (!normalizedStopId) return null;
    const current = rowsByStop.get(normalizedStopId) || {
      stopId: normalizedStopId,
      label: normalizedStopId,
      countyKey: "",
      countyLabel: "",
      stopViews: 0,
      directionsClicks: 0,
      verifiedStamps: 0,
      totalInteractions: 0,
    };
    const nextLabel = String(label || "").trim();
    const next = {
      ...current,
      ...metadata,
      stopId: normalizedStopId,
      label: current.label === normalizedStopId && nextLabel ? nextLabel : current.label,
    };
    if (nextLabel && current.label === normalizedStopId) next.label = nextLabel;
    rowsByStop.set(normalizedStopId, next);
    return next;
  };

  const verifiedRows = authoritativeEventDayMetrics?.allStopsByVerifiedStamps || [];
  verifiedRows.forEach((row) => {
    const stopId = getStopIdFromRow(row);
    if (!stopId) return;
    const item = ensureRow(stopId, row.label, {
      countyKey: row.countyKey || "",
      countyLabel: row.countyLabel || "",
    });
    if (item) item.verifiedStamps = Number(row.count) || 0;
  });

  events
    .filter((event) => event.relatedId && (
      event.action === FALL_CRAWL_ANALYTICS_ACTIONS.stopView ||
      event.action === FALL_CRAWL_ANALYTICS_ACTIONS.directionsClick
    ))
    .forEach((event) => {
      const item = ensureRow(event.relatedId, getFallCrawlStopLabel(event));
      if (!item) return;
      if (event.action === FALL_CRAWL_ANALYTICS_ACTIONS.stopView) item.stopViews += 1;
      if (event.action === FALL_CRAWL_ANALYTICS_ACTIONS.directionsClick) item.directionsClicks += 1;
    });

  return [...rowsByStop.values()]
    .map((row) => ({
      ...row,
      totalInteractions: row.stopViews + row.directionsClicks + row.verifiedStamps,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const buildHourlyFallCrawlActivity = (events = [], timeZone = FALL_CRAWL_EVENT_DAY_TIME_ZONE) => {
  const counts = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    label: `${String(hour).padStart(2, "0")}:00`,
    count: 0,
  }));

  events.forEach((event) => {
    const createdAt = toMillis(event.createdAt);
    if (!createdAt) return;
    const { hour } = getZonedDateParts(createdAt, timeZone);
    if (Number.isInteger(hour) && counts[hour]) counts[hour].count += 1;
  });

  return counts;
};

export const buildFallCrawlInteractionAnalytics = (
  events = [],
  period = {},
  authoritativeEventDayMetrics = null
) => {
  if (period?.queryMode === "daily") {
    return {
      summaryOnly: true,
      uniquePublicVisitors: null,
      interactiveMapOpens: null,
      stopViews: null,
      directionsClicks: null,
      backroadsBookOpens: null,
      localLiveOpens: null,
      routeInteractions: null,
      topStopsByStopViews: [],
      topStopsByDirections: [],
      fullStopRows: [],
      hourlyActivity: [],
      authoritativeEventDayMetrics,
    };
  }

  const startMs = Number.isFinite(period?.startMs) ? period.startMs : 0;
  const endMs = Number.isFinite(period?.endMs) ? period.endMs : Number.MAX_SAFE_INTEGER;
  const fallCrawlEvents = events.filter((event) => {
    if (!isFallCrawlInteractionEvent(event)) return false;
    const createdAt = toMillis(event.createdAt);
    return createdAt >= startMs && createdAt <= endMs;
  });
  const anonymousFallCrawlEvents = fallCrawlEvents.filter((event) => !event.userId && event.isLoggedIn !== true);

  return {
    summaryOnly: false,
    uniquePublicVisitors: countUniqueSessions(anonymousFallCrawlEvents),
    interactiveMapOpens: countFallCrawlAction(fallCrawlEvents, FALL_CRAWL_ANALYTICS_ACTIONS.mapOpen),
    stopViews: countFallCrawlAction(fallCrawlEvents, FALL_CRAWL_ANALYTICS_ACTIONS.stopView),
    directionsClicks: countFallCrawlAction(fallCrawlEvents, FALL_CRAWL_ANALYTICS_ACTIONS.directionsClick),
    backroadsBookOpens: countFallCrawlAction(fallCrawlEvents, FALL_CRAWL_ANALYTICS_ACTIONS.backroadsBookOpen),
    localLiveOpens: countFallCrawlAction(fallCrawlEvents, FALL_CRAWL_ANALYTICS_ACTIONS.localLiveOpen),
    routeInteractions: countFallCrawlAction(fallCrawlEvents, FALL_CRAWL_ANALYTICS_ACTIONS.routeInteraction),
    topStopsByStopViews: buildTopFallCrawlStops(fallCrawlEvents, FALL_CRAWL_ANALYTICS_ACTIONS.stopView),
    topStopsByDirections: buildTopFallCrawlStops(fallCrawlEvents, FALL_CRAWL_ANALYTICS_ACTIONS.directionsClick),
    fullStopRows: buildFallCrawlFullStopRows(fallCrawlEvents, authoritativeEventDayMetrics),
    hourlyActivity: buildHourlyFallCrawlActivity(fallCrawlEvents, period?.timeZone || FALL_CRAWL_EVENT_DAY_TIME_ZONE),
    authoritativeEventDayMetrics,
  };
};

export const buildPublicVisitorAnalytics = (events = [], days = 7, now = Date.now()) => {
  const period = typeof days === "object" && days?.startMs !== undefined
    ? days
    : getPublicVisitorReportingPeriod(days, now);
  const recent = events.filter((event) => {
    const createdAt = toMillis(event.createdAt);
    return createdAt >= period.startMs && createdAt <= period.endMs;
  });
  const anonymousEvents = recent.filter((event) => !event.userId && event.isLoggedIn !== true);
  const publicEvents = anonymousEvents.filter((event) => PUBLIC_VISITOR_EVENT_TYPES.has(event.eventType));
  const protectedActions = anonymousEvents.filter(isPublicVisitorActionEvent);
  const registrations = recent.filter((event) => event.eventType === "registration_completed");
  const featureRows = PUBLIC_FEATURES.map((feature) => {
    const featureEvents = anonymousEvents.filter((event) => featureForEvent(event) === feature.key);
    const viewEvents = featureEvents.filter((event) => feature.viewEvents.includes(event.eventType));
    const actions = featureEvents.filter(isPublicVisitorActionEvent).length;
    return {
      ...feature,
      visitors: countUniqueSessions(viewEvents),
      views: viewEvents.length,
      actions,
    };
  }).sort((a, b) => b.views + b.actions - (a.views + a.actions) || a.label.localeCompare(b.label));

  const visitorSessionIds = new Set(
    publicEvents.map((event) => event.sessionId).filter(Boolean)
  );
  const sourceBySession = new Map(
    [...visitorSessionIds].map((sessionId) => [sessionId, "Unknown"])
  );

  anonymousEvents
    .filter((event) => visitorSessionIds.has(event.sessionId))
    .reduce((sessions, event) => {
      if (!event.sessionId) return sessions;
      const source = normalizeSource(event);
      const existing = sessions.get(event.sessionId);
      if (!existing || existing === "Unknown" || (existing === "Direct" && source !== "Direct")) {
        sessions.set(event.sessionId, source);
      }
      return sessions;
    }, sourceBySession);

  const sourceCounts = [...sourceBySession.values()].reduce((counts, source) => {
    counts[source] = (counts[source] || 0) + 1;
    return counts;
  }, {});

  return {
    publicVisitors: countUniqueSessions(publicEvents),
    publicPageViews: anonymousEvents.filter((event) => event.eventType === "public_page_view").length,
    signupInterest: anonymousEvents.filter((event) => event.eventType === "signup_click").length,
    registrations: registrations.length,
    featureRows,
    funnel: {
      publicVisitors: countUniqueSessions(publicEvents),
      viewedPublicContent: countUniqueSessions(publicEvents),
      protectedActions: countUniqueSessions(protectedActions),
      signupClicks: countUniqueSessions(
        anonymousEvents.filter((event) => event.eventType === "signup_click")
      ),
      registrations: registrations.length,
    },
    sources: Object.entries(sourceCounts)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
  };
};

export const buildPublicVisitorDailyAnalytics = (dailyRollups = [], period = {}) => {
  const totals = dailyRollups.reduce((acc, item) => {
    Object.entries(item || {}).forEach(([key, value]) => {
      if (["id", "date", "updatedAt"].includes(key)) return;
      const numberValue = Number(value || 0);
      if (!Number.isFinite(numberValue)) return;
      acc[key] = (acc[key] || 0) + numberValue;
    });
    return acc;
  }, {});

  return {
    summaryOnly: true,
    summaryUnavailableLabel: "Summary only",
    publicVisitors: null,
    publicPageViews: null,
    signupInterest: null,
    registrations: totals.signups || 0,
    featureRows: PUBLIC_FEATURES.map((feature) => ({
      ...feature,
      visitors: null,
      views: null,
      actions: null,
    })),
    funnel: {
      publicVisitors: null,
      viewedPublicContent: totals.visits || 0,
      protectedActions: null,
      signupClicks: null,
      registrations: totals.signups || 0,
    },
    sources: [],
    dailyRollup: {
      daysWithData: dailyRollups.length,
      rangeLabel: period.label || "Selected range",
      totals,
      visits: totals.visits || 0,
      signups: totals.signups || 0,
      requestToBuyStarts: totals.requestToBuyStarts || 0,
      requestToBuyCompletes: totals.requestToBuyCompletes || 0,
      standCreates: totals.standCreates || 0,
      productCreates: totals.productCreates || 0,
      jobPostCreates: totals.jobPostCreates || 0,
      communityPostCreates: totals.communityPostCreates || 0,
      reports: totals.reports || 0,
      supportRequests: totals.supportRequests || 0,
    },
  };
};

export { PUBLIC_FEATURES };
